import { useState } from "preact/hooks";
import { required, styled, ToucanButton } from "toucan";
import { QrFrame } from "./QrFrame.tsx";
import { linkedinHref, whatsappHref } from "../model.ts";
import { go } from "../routes.ts";
import { store } from "../state.ts";
import type { QrService } from "../model.ts";

/* A card the height of the name and the photo tile beside it, with the code
   filling what the tabs leave. Sized in rem, as the fields beside it are. */
const Card = styled("aside")`
  display: flex;
  flex: none;
  flex-direction: column;
  width: 10rem;
  border: 1px solid var(--pico-muted-border-color);
  border-radius: var(--pico-border-radius);
  background: var(--toucan-surface);
  overflow: hidden;

  /* The services as tabs along the top, the one showing underlined in the
     accent. */
  .qr-tabs {
    display: flex;
  }

  .qr-tabs button,
  .qr-service-heading {
    flex: 1;
    margin: 0;
    padding: 0.35rem 0.25rem;
    border: none;
    border-bottom: 2px solid transparent;
    border-radius: 0;
    background: none;
    color: var(--toucan-muted);
    /* The one place under the floor: a service's name on a tab a thumb
       does not aim at, on a card that is already the width the code needs. */
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.2;
    text-align: center;
    box-shadow: none;
  }

  .qr-tabs button[aria-selected="true"] {
    border-bottom-color: var(--pico-primary);
    color: var(--pico-primary);
  }

  [data-qr] {
    display: grid;
    flex: 1;
    place-items: center;
    padding: 0.4rem;
  }

  /* The frame's own compact width is a row's worth; here the card is the
     measure. */
  [data-qr][data-compact] svg {
    width: 100%;
    max-width: 9rem;
  }

  /* With no link set, the card is the way to set one. */
  .qr-nudge {
    display: grid;
    flex: 1;
    gap: 0.5rem;
    place-content: center;
    padding: 0.5rem;
    text-align: center;
  }

  .qr-nudge p {
    margin: 0;
    font-size: 0.85rem;
  }
`;

/** Settings, where the links behind the codes are kept. */
function openSettings(): void {
  go("settings");
}

/** The services with a share-back link configured, in tab order. */
function myQrLinks() {
  const links: { service: QrService; label: string; href: string }[] = [];
  const me = store().me;
  if (me.whatsapp !== undefined) {
    links.push({
      service: "whatsapp",
      label: "WhatsApp",
      href: whatsappHref(me.whatsapp),
    });
  }
  if (me.linkedin !== undefined) {
    links.push({
      service: "linkedin",
      label: "LinkedIn",
      href: linkedinHref(me.linkedin),
    });
  }
  return links;
}

/**
 * "My QR" on the capture form: a code the person you've just met can scan to
 * reach you, on a card beside the name and the photos rather than above them,
 * so the form starts at the top of the screen. With two links the card has
 * two tabs, and with one it shows that link's name instead. With none it
 * nudges you to set one.
 */
export function MyQr() {
  const [service, setService] = useState<QrService>("whatsapp");
  const links = myQrLinks();
  if (links.length === 0) {
    return (
      <Card aria-label="My QR">
        <div class="qr-nudge">
          <p class="muted">A code for them to scan.</p>
          <ToucanButton small onClick={openSettings}>
            Set QR codes
          </ToucanButton>
        </div>
      </Card>
    );
  }
  // The chosen service is WhatsApp until something is picked, and that may not
  // be one of the configured ones. The first configured link is shown then.
  const active =
    links.find((link) => link.service === service) ??
    required(links[0], "link");
  return (
    <Card aria-label="My QR">
      {links.length > 1 ? (
        <div class="qr-tabs" role="tablist" aria-label="My QR">
          {links.map((link) => (
            <button
              key={link.service}
              type="button"
              role="tab"
              aria-selected={link.service === active.service}
              onClick={() => {
                setService(link.service);
              }}
            >
              {link.label}
            </button>
          ))}
        </div>
      ) : (
        <p class="qr-service-heading">{active.label}</p>
      )}
      <QrFrame href={active.href} compact />
    </Card>
  );
}
